import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";

const LANGUAGE_ALIASES = {
  js: "javascript",
  jsx: "javascript",
  ts: "typescript",
  tsx: "typescript",
  py: "python",
  c: "cpp",
  "c++": "cpp",
  cs: "csharp",
  sh: "bash",
  shell: "bash",
  html: "markup",
  xml: "markup",
  md: "markdown",
  yml: "yaml",
  dockerfile: "docker",
  ps1: "powershell",
  rs: "rust",
  pl: "perl",
};

const LANGUAGE_LOADERS = {
  python: () => import("react-syntax-highlighter/dist/esm/languages/prism/python"),
  javascript: () => import("react-syntax-highlighter/dist/esm/languages/prism/javascript"),
  java: () => import("react-syntax-highlighter/dist/esm/languages/prism/java"),
  csharp: () => import("react-syntax-highlighter/dist/esm/languages/prism/csharp"),
  cpp: () => import("react-syntax-highlighter/dist/esm/languages/prism/cpp"),
  ruby: () => import("react-syntax-highlighter/dist/esm/languages/prism/ruby"),
  swift: () => import("react-syntax-highlighter/dist/esm/languages/prism/swift"),
  go: () => import("react-syntax-highlighter/dist/esm/languages/prism/go"),
  kotlin: () => import("react-syntax-highlighter/dist/esm/languages/prism/kotlin"),
  php: () => import("react-syntax-highlighter/dist/esm/languages/prism/php"),
  sql: () => import("react-syntax-highlighter/dist/esm/languages/prism/sql"),
  typescript: () => import("react-syntax-highlighter/dist/esm/languages/prism/typescript"),
  json: () => import("react-syntax-highlighter/dist/esm/languages/prism/json"),
  bash: () => import("react-syntax-highlighter/dist/esm/languages/prism/bash"),
  css: () => import("react-syntax-highlighter/dist/esm/languages/prism/css"),
  markup: () => import("react-syntax-highlighter/dist/esm/languages/prism/markup"),
  yaml: () => import("react-syntax-highlighter/dist/esm/languages/prism/yaml"),
  markdown: () => import("react-syntax-highlighter/dist/esm/languages/prism/markdown"),
  docker: () => import("react-syntax-highlighter/dist/esm/languages/prism/docker"),
  powershell: () => import("react-syntax-highlighter/dist/esm/languages/prism/powershell"),
  rust: () => import("react-syntax-highlighter/dist/esm/languages/prism/rust"),
  dart: () => import("react-syntax-highlighter/dist/esm/languages/prism/dart"),
  scala: () => import("react-syntax-highlighter/dist/esm/languages/prism/scala"),
  perl: () => import("react-syntax-highlighter/dist/esm/languages/prism/perl"),
  lua: () => import("react-syntax-highlighter/dist/esm/languages/prism/lua"),
};

const registeredLanguages = new Set();

const normalizeLanguage = (language) => {
  if (!language) return null;
  const lower = language.toLowerCase();
  return LANGUAGE_ALIASES[lower] || lower;
};

const registerAliasIfNeeded = (alias, languageModule) => {
  if (!alias || registeredLanguages.has(alias)) return;
  SyntaxHighlighter.registerLanguage(alias, languageModule);
  registeredLanguages.add(alias);
};

export const isLanguageRegistered = (language) => {
  const normalized = normalizeLanguage(language);
  return normalized ? registeredLanguages.has(normalized) : false;
};

export const registerLanguageAsync = async (language) => {
  const normalized = normalizeLanguage(language);
  if (!normalized) return false;
  if (registeredLanguages.has(normalized)) return true;

  const loader = LANGUAGE_LOADERS[normalized];
  if (!loader) return false;

  const module = await loader();
  const languageModule = module.default || module;
  SyntaxHighlighter.registerLanguage(normalized, languageModule);
  registeredLanguages.add(normalized);

  if (language && language.toLowerCase() !== normalized) {
    registerAliasIfNeeded(language.toLowerCase(), languageModule);
  }

  Object.entries(LANGUAGE_ALIASES).forEach(([alias, target]) => {
    if (target === normalized) {
      registerAliasIfNeeded(alias, languageModule);
    }
  });

  return true;
};
